/**
 * Content-changing v2 actions: `counter` (count-up for prices) and
 * `change-text` (mid-scene swap, instant or a rolling character replace over
 * the block's duration). These resolve to the layer's text content at a
 * frame rather than to CSS; `block-style-at.ts` handles every styling action.
 */
import type { AnimationBlock } from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
import { blockProgress } from "./block-style-at";
import { clamp01 } from "./easings";

/** Rolling character replace: the new text grows from the left while the old
 * text is consumed at the same relative rate — progress 0 is all `from`,
 * progress 1 all `to`. */
function transitionText(from: string, to: string, progress: number): string {
  return (
    to.slice(0, Math.round(to.length * progress)) +
    from.slice(Math.round(from.length * progress))
  );
}

function formatCounterValue(
  value: number,
  params: Extract<AnimationBlock, { action: "counter" }>["params"],
): string {
  const decimals = params.decimals ?? 0;
  // Fixed locale: same JSON must render the same pixels on every machine.
  const formatted = value.toLocaleString("en-US", {
    useGrouping: params.separator !== false,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${params.prefix ?? ""}${formatted}${params.suffix ?? ""}`;
}

/**
 * The text one block contributes at `frame`, or null when it contributes
 * nothing (not a content action, or a swap that has not happened yet).
 * `current` is the text the swap transitions away from — the layer's
 * authored text, or an earlier content block's output.
 */
export function blockContentAt(
  block: AnimationBlock,
  frame: number,
  current = "",
): string | null {
  switch (block.action) {
    case "counter": {
      const { from, to } = block.params;
      // Holds `from` before the block; holds `to` after — a counted-up price
      // must not snap back to the authored text.
      const progress = blockProgress(block, frame);
      return formatCounterValue(from + (to - from) * progress, block.params);
    }
    case "change-text": {
      if (frame < block.start) return null;
      if (block.duration <= 0 || frame >= block.start + block.duration) {
        return block.params.to;
      }
      // Expressive easings overshoot [0, 1]; slicing needs a clamped value.
      const progress = clamp01(blockProgress(block, frame));
      return transitionText(current, block.params.to, progress);
    }
    default:
      return null;
  }
}

/**
 * The layer's text override at one frame; the last contributing block in
 * start order wins, and each block transitions from the previous
 * contribution (`authored` is the layer's own plain text, the starting point
 * of the chain).
 */
export function blockContentForLayer(
  blocks: readonly AnimationBlock[] | undefined,
  frame: number,
  authored = "",
): string | null {
  if (!blocks?.length) return null;
  let content: string | null = null;
  const sorted = [...blocks].sort((a, b) => a.start - b.start);
  for (const block of sorted) {
    content = blockContentAt(block, frame, content ?? authored) ?? content;
  }
  return content;
}
